/**
 * Decides whether keyboard movement must be suppressed (A09: typing in chat never moves the
 * avatar; docs/01 section 7: no movement while focus is in an input, modal, chat or browser
 * shortcut). Pure so it can be unit-tested with plain objects.
 */
export interface FocusTargetLike {
  tagName?: string;
  isContentEditable?: boolean;
  getAttribute?(name: string): string | null;
  closest?(selector: string): unknown;
}

const TEXT_INPUT_TYPES = new Set(["text", "search", "email", "password", "number", "tel", "url", "date", "datetime-local", "month", "week", "time", ""]);

/** Any element where typing letters is expected, or that lives inside a dialog / movement-blocking region. */
export function isTypingTarget(el: FocusTargetLike | null | undefined): boolean {
  if (!el) return false;
  const tag = (el.tagName ?? "").toUpperCase();
  if (tag === "TEXTAREA" || tag === "SELECT") return true;
  if (tag === "INPUT") {
    const type = (el.getAttribute?.("type") ?? "").toLowerCase();
    if (TEXT_INPUT_TYPES.has(type)) return true;
    // A radio or checkbox inside a dialog (a quiz answer) uses the arrow keys too: fall through to the dialog check.
  }
  if (el.isContentEditable) return true;
  const role = el.getAttribute?.("role");
  if (role === "textbox" || role === "combobox" || role === "searchbox") return true;
  if (typeof el.closest === "function" && el.closest('[role="dialog"], [data-movement-block="true"]')) return true;
  return false;
}

export interface MovementContext {
  activeElement: FocusTargetLike | null | undefined;
  modalOpen: boolean;
  chatFocused: boolean;
  documentVisible: boolean;
  windowFocused: boolean;
  walkMode: boolean;
}

/** True when keyboard/joystick intent must be treated as zero. */
export function shouldBlockMovement(ctx: MovementContext): boolean {
  if (!ctx.walkMode) return true;
  if (!ctx.documentVisible || !ctx.windowFocused) return true;
  if (ctx.modalOpen || ctx.chatFocused) return true;
  return isTypingTarget(ctx.activeElement);
}

/** Keys the town must leave to the browser/UI even in walk mode (shortcuts, tab focus, escape). */
export function isReservedKey(ev: { code: string; ctrlKey: boolean; metaKey: boolean; altKey: boolean }): boolean {
  if (ev.ctrlKey || ev.metaKey || ev.altKey) return true;
  return ev.code === "Tab" || ev.code === "Escape" || ev.code === "F5" || ev.code === "F12";
}
